import type { Insight } from "@tandemdiabetes/sugarmate-api-react-client"
import {
  DashFillIcon,
  IconButton,
  useToasterContext,
  SortableGridItem,
  useSortableGridContext,
} from "@tandemdiabetes/sugarmate-react-components"
import classNames from "classnames"
import {
  useCallback,
  useMemo,
} from "react"
import type { MouseEventHandler } from "react"
import { useEditInsightModalContext } from "../EditInsightModal"
import { useInsightsContext } from "../InsightsContext"
import styles from "./Tile.module.scss"
import { TileChildren } from "./TileChildren"
import { useOverlayLoader } from "src/contexts"
import { useRemoveInsight } from "src/hooks"
import { RemoveInsightErrorToast } from "src/toasts"
import { InsightsState } from "src/types"
import { injectProps } from "src/utils"

interface Props {
  id: string;
  insight: Insight;
  index: number;
}

// the duration of the wiggle animation
export const animationDurationInSeconds = 0.3

export function SortableTile(props: Props) {
  const {
    id,
    insight,
    index,
  } = props

  const removeInsight = useRemoveInsight()
  const { openEditInsightModal } = useEditInsightModalContext()
  const { showToast } = useToasterContext()
  const { setIsOverlayLoaderVisible } = useOverlayLoader()
  const { insightsState } = useInsightsContext()
  const { activeId } = useSortableGridContext()

  const deleteInsight: MouseEventHandler<HTMLButtonElement> = useCallback(
    () => {
      void (async () => {
        const ToastComponent = injectProps(
          RemoveInsightErrorToast,
          { insightKey: insight.key },
        )

        setIsOverlayLoaderVisible(true)

        try {
          if (!removeInsight) {
            throw new Error("cannot remove insight")
          }

          await removeInsight(insight)
        } catch {
          showToast(
            "remove-insight-error-toast",
            ToastComponent,
          )
        }

        setIsOverlayLoaderVisible(false)
      })()
    },
    [
      insight,
      removeInsight,
      showToast,
      setIsOverlayLoaderVisible,
    ],
  )

  const onClick = useCallback(
    () => {
      openEditInsightModal(insight)
    },
    [
      openEditInsightModal,
      insight,
    ],
  )

  const isEditing = insightsState === InsightsState.Editing
  const isReordering = insightsState === InsightsState.Reordering

  const iconButtonClassName = classNames(
    styles.delete,
    { [styles.deleteHidden]: !isEditing || !!activeId },
  )

  const animationStyle = useMemo(
    () => {
      /*
       * 1.73 is just a small number that will divide indexes unevenly to
       * ensure that they have unique animation delays
       */
      const delay = animationDurationInSeconds * index / 1.73

      // setting a negative animation delay immediately applies the animation
      return {
        animationDelay: `${-1 * delay}s`,
        animationDuration: `${animationDurationInSeconds}s`,
      }
    },
    [index],
  )

  return (
    <div
      className={styles.container}
      id="tile"
    >
      <IconButton
        onClick={deleteInsight}
        stylePreset="withoutBackground"
        className={iconButtonClassName}
        disabled={!isEditing || !removeInsight}
        id={`remove-button-${insight.key}`}
      >
        <DashFillIcon />
      </IconButton>
      {isReordering ? (
        <SortableGridItem
          id={id}
          className={styles.sortableTileButton}
        >
          <TileChildren
            {...props}
            className={styles.wiggling}
            style={animationStyle}
          />
        </SortableGridItem>
      ) : (
        <button
          id={id}
          onClick={onClick}
          className={styles.tileButton}
          type="button"
        >
          <TileChildren {...props} />
        </button>
      )}
    </div>
  )
}
